import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import howIEatService from '../services/howIEatService'
import FoodPhotoLogSection, { FoodPhotoLogHandle } from '../components/FoodPhotoLogSection'
import HowIEatSegmentTabs, { HowIEatTabId } from '../components/how-i-eat/HowIEatSegmentTabs'
import HowIEatPlanTab from '../components/how-i-eat/HowIEatPlanTab'
import HowIEatStatsTab from '../components/how-i-eat/HowIEatStatsTab'
import HowIEatTodayQuickActions, { TodayQuickPanel } from '../components/how-i-eat/HowIEatTodayQuickActions'
import {
  HowIEatToday,
  HowIEatWeightEntry,
  HowIEatFastingStatus,
  HowIEatFastingHistoryEntry,
  HowIEatAchievement,
} from '../types/howIEat'
import {
  checkAchievements,
  computeFastingProgress,
  formatFastingDuration,
} from '../utils/howIEatHelpers'
import { useTranslation } from '../hooks/use-i18n'

const MacroRow = ({
  label,
  current,
  goal,
  unit = '',
}: {
  label: string
  current: number
  goal: number
  unit?: string
}) => {
  const pct = goal > 0 ? Math.min(100, Math.round((current / goal) * 100)) : 0
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-white/70">{label}</span>
        <span className="text-white font-medium">
          {Math.round(current)}
          {unit}/{goal}
          {unit}
        </span>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full bg-emerald-500 rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

const mealLabel = (type: string) => type.charAt(0).toUpperCase() + type.slice(1)

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr + 'T12:00:00')
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const HowIEatDetail: React.FC = () => {
  const { t } = useTranslation()
  const { spaceId } = useParams<{ spaceId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const initialTab: HowIEatTabId =
    tabParam === 'plan' || tabParam === 'stats' ? tabParam : 'today'
  const [activeTab, setActiveTab] = useState<HowIEatTabId>(initialTab)
  const [today, setToday] = useState<HowIEatToday | null>(null)
  const [weightHistory, setWeightHistory] = useState<HowIEatWeightEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [waterLoading, setWaterLoading] = useState(false)
  const [waterInput, setWaterInput] = useState('')
  const [weightInput, setWeightInput] = useState('')
  const [weightLoading, setWeightLoading] = useState(false)
  const [fasting, setFasting] = useState<HowIEatFastingStatus | null>(null)
  const [fastingHistory, setFastingHistory] = useState<HowIEatFastingHistoryEntry[]>([])
  const [fastingBusy, setFastingBusy] = useState(false)
  const [tick, setTick] = useState(0)
  const [expandedPanel, setExpandedPanel] = useState<TodayQuickPanel>(null)
  const achievementsRef = useRef<HowIEatAchievement[]>([])
  const foodPhotoRef = useRef<FoodPhotoLogHandle>(null)

  const refreshAchievements = useCallback(async () => {
    achievementsRef.current = await checkAchievements(
      () => howIEatService.getAchievements(),
      achievementsRef.current,
    )
  }, [])

  const load = useCallback(async () => {
    try {
      setIsLoading(true)
      const [todayData, weights, fastingData, history, achievements] = await Promise.all([
        howIEatService.getToday(),
        howIEatService.getWeightHistory(14),
        howIEatService.getFastingStatus(),
        howIEatService.getFastingHistory(5),
        howIEatService.getAchievements(),
      ])
      setToday(todayData)
      setWeightHistory(weights)
      setFasting(fastingData)
      setFastingHistory(history)
      achievementsRef.current = achievements
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.howIEatLoadFailed'))
      navigate('/dashboard')
    } finally {
      setIsLoading(false)
    }
  }, [navigate, t])

  useEffect(() => {
    if (spaceId) load()
  }, [spaceId, load])

  useEffect(() => {
    if (tabParam === 'plan' || tabParam === 'stats') {
      setActiveTab(tabParam)
    }
  }, [tabParam])

  const handleTabChange = (tab: HowIEatTabId) => {
    setActiveTab(tab)
    if (tab === 'today') {
      setSearchParams({})
    } else {
      setSearchParams({ tab })
    }
  }

  useEffect(() => {
    if (!fasting?.active) return
    const id = setInterval(() => setTick((t) => t + 1), 30000)
    return () => clearInterval(id)
  }, [fasting?.active])

  const liveFasting =
    fasting?.active && fasting.session
      ? computeFastingProgress(fasting.session.startedAt, fasting.session.targetHours)
      : null
  void tick

  const logWater = async (ml: number) => {
    if (!ml || ml < 1) {
      toast.error(t('toast.validWaterAmount'))
      return
    }
    try {
      setWaterLoading(true)
      await howIEatService.logWater(ml)
      toast.success(t('toast.waterLogged', { ml: String(ml) }))
      setWaterInput('')
      await load()
      await refreshAchievements()
    } catch {
      toast.error(t('toast.waterLogFailed'))
    } finally {
      setWaterLoading(false)
    }
  }

  const logWeight = async () => {
    const w = parseFloat(weightInput)
    if (!w || w < 20) {
      toast.error(t('toast.validWeight'))
      return
    }
    try {
      setWeightLoading(true)
      await howIEatService.logWeight(w)
      toast.success(t('toast.weightLogged'))
      setWeightInput('')
      await load()
      await refreshAchievements()
    } catch {
      toast.error(t('toast.weightLogFailed'))
    } finally {
      setWeightLoading(false)
    }
  }

  const deleteMeal = async (mealId: string) => {
    try {
      await howIEatService.deleteMeal(mealId)
      toast.success(t('toast.mealRemoved'))
      await load()
    } catch {
      toast.error(t('toast.mealDeleteFailed'))
    }
  }

  const togglePanel = (panel: Exclude<TodayQuickPanel, null>) => {
    setExpandedPanel((current) => (current === panel ? null : panel))
  }

  const renderFastingPanel = () => {
    if (!fasting) return null
    return (
      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-4 mb-4">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-white font-semibold">{t('howIEat.fasting.title')}</h2>
          {liveFasting && (
            <span className={`text-sm font-medium ${liveFasting.goalReached ? 'text-amber-400' : 'text-emerald-400'}`}>
              {liveFasting.progressPct}%
            </span>
          )}
        </div>
        {fasting.active && fasting.session && liveFasting ? (
          <>
            <p className="text-white/60 text-sm mb-1">
              {formatFastingDuration(liveFasting.elapsedHours)} elapsed ·{' '}
              {liveFasting.goalReached
                ? t('common.goalReached')
                : `${formatFastingDuration(liveFasting.remainingHours)} ${t('common.left')}`}{' '}
              ({t('common.goal')} {fasting.session.targetHours}h)
            </p>
            <p className="text-white/40 text-xs mb-3">
              {t('common.startedAt', { time: new Date(fasting.session.startedAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) })}
            </p>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-3">
              <div
                className={`h-full ${liveFasting.goalReached ? 'bg-amber-500' : 'bg-emerald-500'}`}
                style={{ width: `${liveFasting.progressPct}%` }}
              />
            </div>
            <button
              type="button"
              disabled={fastingBusy}
              onClick={async () => {
                try {
                  setFastingBusy(true)
                  const goalReached = liveFasting.goalReached
                  const s = await howIEatService.endFasting()
                  setFasting(s)
                  const history = await howIEatService.getFastingHistory(5)
                  setFastingHistory(history)
                  toast.success(goalReached ? t('toast.fastComplete') : t('toast.fastingEnded'))
                  await refreshAchievements()
                } catch {
                  toast.error(t('toast.endFastFailed'))
                } finally {
                  setFastingBusy(false)
                }
              }}
              className="w-full py-2 rounded-lg border border-white/20 text-white text-sm disabled:opacity-60"
            >
              {t('common.endFast')}
            </button>
          </>
        ) : (
          <button
            type="button"
            disabled={fastingBusy}
            onClick={async () => {
              try {
                setFastingBusy(true)
                const s = await howIEatService.startFasting()
                setFasting(s)
                toast.success(t('toast.fastingStarted'))
              } catch (err: any) {
                toast.error(err.response?.data?.message || t('toast.startFastFailed'))
              } finally {
                setFastingBusy(false)
              }
            }}
            className="w-full py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-60"
          >
            {t('common.startFast', { hours: String(fasting.targetHours) })}
          </button>
        )}
        {fastingHistory.length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <p className="text-white/50 text-xs mb-2">{t('common.recentFasts')}</p>
            <div className="space-y-2">
              {fastingHistory.slice(0, 3).map((s) => (
                <div key={s.id} className="flex justify-between text-xs">
                  <span className="text-white/60">
                    {new Date(s.startedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </span>
                  <span className={s.goalMet ? 'text-emerald-400' : 'text-white/50'}>
                    {s.durationHours != null ? `${s.durationHours}h` : '—'} / {s.targetHours}h
                    {s.goalMet ? ' ✓' : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderBodyPanel = () => (
    <div className="mb-6">
      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-4 mb-4">
        <p className="text-white/70 text-sm mb-2">{t('common.logWeightKg')}</p>
        <div className="flex gap-2">
          <input
            type="number"
            step="0.1"
            value={weightInput}
            onChange={(e) => setWeightInput(e.target.value)}
            placeholder={t('howIEat.weightPlaceholder')}
            className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
          />
          <button
            type="button"
            disabled={weightLoading}
            onClick={logWeight}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-white text-sm font-semibold disabled:opacity-60"
          >
            {t('common.save')}
          </button>
        </div>
      </div>

      {weightHistory.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-white mb-3">{t('howIEat.weightHistory')}</h2>
          <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl divide-y divide-white/10">
            {weightHistory.map((entry) => (
              <div key={entry.id} className="flex justify-between items-center px-4 py-3 text-sm">
                <span className="text-white/70">{formatDate(entry.entryDate)}</span>
                <span className="text-white font-medium">{entry.weightKg} kg</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )

  if (isLoading && activeTab === 'today') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500" />
      </div>
    )
  }

  return (
    <div className="min-h-screen pb-12">
      <main className="max-w-lg mx-auto px-4 py-6 sm:py-8">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6"
        >
          {t('common.backToDashboardLower')}
        </Link>

        <div className="mb-2">
          <h1 className="text-2xl font-bold text-white">{t('howIEat.title')}</h1>
          <p className="text-white/50 text-sm mt-1">{t('howIEat.todaysSummary')}</p>
        </div>

        <HowIEatSegmentTabs activeTab={activeTab} onChange={handleTabChange} />

        {activeTab === 'plan' ? <HowIEatPlanTab /> : null}
        {activeTab === 'stats' ? <HowIEatStatsTab /> : null}

        {activeTab === 'today' && today ? (
          <>
            <HowIEatTodayQuickActions
              activePanel={expandedPanel}
              fastingActive={!!fasting?.active}
              onTakePhoto={() => {
                setExpandedPanel('photo')
                requestAnimationFrame(() => foodPhotoRef.current?.openFilePicker())
              }}
              onAddMeal={() => navigate(`/how-i-eat/${spaceId}/log-meal`)}
              onFasting={() => togglePanel('fasting')}
              onOutOfBody={() => togglePanel('body')}
            />

            <FoodPhotoLogSection
              ref={foodPhotoRef}
              compact
              hidden={expandedPanel !== 'photo'}
              onAnalyzed={(items, notes) => {
                navigate(`/how-i-eat/${spaceId}/log-meal`, { state: { draftItems: items, draftNotes: notes } })
              }}
            />

            {expandedPanel === 'fasting' ? renderFastingPanel() : null}
            {expandedPanel === 'body' ? renderBodyPanel() : null}

        <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-5 mb-4">
          <div className="flex gap-4 mb-4 text-sm flex-wrap">
            <span className="text-emerald-400 font-semibold">{t('common.dayStreak', { count: String(today.streakDays) })}</span>
            {today.goalWeightKg != null && (
              <span className="text-white/60">{t('common.goalWeight', { weight: String(today.goalWeightKg) })}</span>
            )}
            {today.latestWeightKg != null && (
              <span className="text-white/60">{t('common.latestWeight', { weight: String(today.latestWeightKg) })}</span>
            )}
          </div>
          <MacroRow label={t('common.calories')} current={today.totals.calories} goal={today.goals.calories} />
          <MacroRow label={t('common.protein')} current={today.totals.proteinG} goal={today.goals.proteinG} unit="g" />
          <MacroRow label={t('common.carbs')} current={today.totals.carbsG} goal={today.goals.carbsG} unit="g" />
          <MacroRow label={t('common.fats')} current={today.totals.fatsG} goal={today.goals.fatsG} unit="g" />
          <MacroRow label={t('common.water')} current={today.totals.waterMl} goal={today.goals.waterMl} unit="ml" />
        </div>

        <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-4 mb-6">
          <p className="text-white/70 text-sm mb-3">{t('common.logWater')}</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {[250, 500].map((ml) => (
              <button
                key={ml}
                type="button"
                disabled={waterLoading}
                onClick={() => logWater(ml)}
                className="px-4 py-2 bg-white/10 hover:bg-white/15 border border-white/10 rounded-lg text-white text-sm font-medium disabled:opacity-60"
              >
                +{ml} ml
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="number"
              value={waterInput}
              onChange={(e) => setWaterInput(e.target.value)}
              placeholder={t('howIEat.waterCustomPlaceholder')}
              className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
            />
            <button
              type="button"
              disabled={waterLoading}
              onClick={() => logWater(parseInt(waterInput, 10))}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-white text-sm font-semibold disabled:opacity-60"
            >
              {t('common.add')}
            </button>
          </div>
        </div>

        <h2 className="text-lg font-bold text-white mb-3">{t('howIEat.todaysMeals')}</h2>
        {today.meals.length === 0 ? (
          <p className="text-white/50 text-sm">{t('howIEat.noMealsToday')}</p>
        ) : (
          <div className="space-y-3">
            {today.meals.map((meal) => (
              <div
                key={meal.id}
                className="bg-[#2a2a2a] border border-white/10 rounded-xl p-4 flex justify-between items-start gap-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold">{mealLabel(meal.mealType)}</p>
                  <p className="text-white/60 text-sm mt-1">
                    {t('howIEat.mealSummary', {
                      calories: String(Math.round(meal.totalCalories)),
                      count: String(meal.items.length),
                      itemLabel: meal.items.length === 1 ? t('common.item') : t('common.items'),
                    })}
                  </p>
                  <p className="text-white/40 text-xs mt-1">
                    P {Math.round(meal.totalProteinG)}g · C {Math.round(meal.totalCarbsG)}g · F{' '}
                    {Math.round(meal.totalFatsG)}g
                  </p>
                </div>
                <div className="flex gap-3 shrink-0">
                  <Link
                    to={`/how-i-eat/${spaceId}/log-meal/${meal.id}`}
                    className="text-emerald-400/90 hover:text-emerald-400 text-sm"
                  >
                    {t('howIEat.editMeal')}
                  </Link>
                  <button
                    type="button"
                    onClick={() => deleteMeal(meal.id)}
                    className="text-red-400/80 hover:text-red-400 text-sm"
                  >
                    {t('howIEat.removeMeal')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        ) : null}
        {activeTab === 'today' && isLoading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500" />
          </div>
        ) : null}
      </main>
    </div>
  )
}

export default HowIEatDetail
